// src/pages/B2B/crm/components/VenuePipelineTable.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { VenueListRow } from '../api/types';
import { PipelineStageBadge } from './PipelineStageBadge';

interface Props {
  rows: VenueListRow[];
  loading: boolean;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
}

function formatBool(v: boolean | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v ? 'Yes' : 'No';
}

function formatText(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

function formatMoney(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

const cellCls = 'px-3 py-2.5 whitespace-nowrap text-gray-300';
const headCls = 'px-3 py-2.5 font-medium whitespace-nowrap';

export function VenuePipelineTable({ rows, loading }: Props) {
  if (loading) {
    return <div className="py-12 text-center text-gray-500 text-sm">Loading venues...</div>;
  }
  if (rows.length === 0) {
    return <div className="py-12 text-center text-gray-500 text-sm">No venues match these filters.</div>;
  }
  return (
    <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 text-xs uppercase tracking-wide">
            <th className={headCls}>Name</th>
            <th className={headCls}>Stage</th>
            <th className={headCls}>Flag</th>
            <th className={headCls}>Outreach Status</th>
            <th className={headCls}>Address</th>
            <th className={headCls}>Type</th>
            <th className={headCls}>Ownership</th>
            <th className={headCls}>Parent Co.</th>
            <th className={headCls}>Locations</th>
            <th className={headCls}>7d Check-ins</th>
            <th className={headCls}>30d Check-ins</th>
            <th className={headCls}>Peak Night</th>
            <th className={headCls}>Social Media</th>
            <th className={headCls}>DocSend Opened</th>
            <th className={headCls}>DocSend Date</th>
            <th className={headCls}>First Contact</th>
            <th className={headCls}>Last Contact</th>
            <th className={headCls}>Last Notes</th>
            <th className={headCls}>Next Action</th>
            <th className={headCls}>Next Action Date</th>
            <th className={headCls}>Pushes Sent</th>
            <th className={headCls}>Tier</th>
            <th className={headCls}>Monthly Revenue</th>
            <th className={headCls}>On Crawl Route</th>
            <th className={headCls}>Warm Intro</th>
            <th className={headCls}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-t border-[#1f1f33] hover:bg-violet-900/10">
              <td className={cellCls}>
                <Link to={`/b2b/crm/venues/${r.id}`} className="text-violet-300 hover:text-violet-200 no-underline font-medium">
                  {r.name}
                </Link>
              </td>
              <td className={cellCls}><PipelineStageBadge stage={r.pipeline_stage} /></td>
              <td className={cellCls}>{r.flag_for_ray ? '🚩' : ''}</td>
              <td className={cellCls}>{formatText(r.corporate_outreach_status)}</td>
              <td className={cellCls}>{formatText(r.address)}</td>
              <td className={cellCls}>{formatText(r.venue_type)}</td>
              <td className={cellCls}>{formatText(r.ownership_type)}</td>
              <td className={cellCls}>{formatText(r.parent_company)}</td>
              <td className={cellCls}>{formatText(r.total_locations)}</td>
              <td className={cellCls}>{formatText(r.last_7d_checkins)}</td>
              <td className={cellCls}>{formatText(r.last_30d_checkins)}</td>
              <td className={cellCls}>{formatText(r.peak_night)}</td>
              <td className={cellCls}>{formatBool(r.social_media_mention)}</td>
              <td className={cellCls}>{formatBool(r.docsend_opened)}</td>
              <td className={cellCls}>{formatDate(r.docsend_open_date)}</td>
              <td className={cellCls}>{formatDate(r.first_contact_date)}</td>
              <td className={cellCls}>{formatDate(r.last_contact_date)}</td>
              <td className={cellCls} title={r.last_contact_notes ?? ''}>
                {r.last_contact_notes ? r.last_contact_notes.slice(0, 40) + (r.last_contact_notes.length > 40 ? '…' : '') : '—'}
              </td>
              <td className={cellCls}>{formatText(r.next_action)}</td>
              <td className={cellCls}>{formatDate(r.next_action_date)}</td>
              <td className={cellCls}>{formatText(r.pushes_sent)}</td>
              <td className={cellCls}>{formatText(r.subscription_tier)}</td>
              <td className={cellCls}>{formatMoney(r.monthly_revenue)}</td>
              <td className={cellCls}>{formatBool(r.on_crawl_route)}</td>
              <td className={cellCls}>{formatText(r.warm_intro_source)}</td>
              <td className={cellCls} title={r.notes ?? ''}>
                {r.notes ? r.notes.slice(0, 40) + (r.notes.length > 40 ? '…' : '') : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
